import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { AiOutlineQrcode } from 'react-icons/ai';
import { FaRegSmile } from 'react-icons/fa';
import { HiOutlineIdentification } from 'react-icons/hi';
import { FaUserAlt } from 'react-icons/fa';
import { FaSuitcaseRolling } from 'react-icons/fa';
import Cookies from 'js-cookie';

// Import your existing components
import Booking from './Booking-Options/Booking';
import SearchReservation from './Facial-Recognition/SearchREservation';
import QRScanner from './QR-code/QRScanner';
import SearchByName from '../../common/SearchByName';
import DueForCheckIn from '../../common/DueForCheckIn';
import ProfileCompletionModal from './ProfileCompletionModal';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Utility function to parse JWT token
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const ReservationOptions = () => {
  const navigate = useNavigate();
  const [activeOption, setActiveOption] = useState('due-for-checkin');
  const [propertyData, setPropertyData] = useState({
    phone: '', // Default value
  });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [guestData, setGuestData] = useState(null);
  const [guestId, setGuestId] = useState(null);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/1`);
        const data = await response.json();
        setPropertyData(data);
      } catch (error) {
        console.error('Error fetching property data:', error);
      }
    };

    fetchPropertyData();
  }, []);

  // Get guest ID from access token
  const getGuestIdFromToken = () => {
    try {
      const accessToken = Cookies.get('access_token');
      if (accessToken) {
        const payload = parseJwt(accessToken);
        if (payload?.sub) {
          console.log('Guest ID from token:', payload.sub);
          return payload.sub;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting guest ID from token:', error);
      return null;
    }
  };

  // Function to fetch guest details from API
  const fetchGuestDetails = async (guestId) => {
    try {
      const accessToken = Cookies.get('access_token');

      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.guest) {
          console.log('Guest details fetched successfully:', data.guest);
          return data.guest;
        }
      } else {
        console.error('Failed to fetch guest details:', response.status);
      }
      return null;
    } catch (error) {
      console.error('Error fetching guest details:', error);
      return null;
    }
  };

  // Function to check if user profile is complete using API data
  const isProfileComplete = (guest) => {
    if (!guest) return false;

    // Check if email exists and is not empty
    const hasEmail = guest.emailid && guest.emailid.trim() !== '';

    // Check if phone exists and is not empty
    const hasPhone = guest.phonenumber && guest.phonenumber.toString().trim() !== '';

    // Check if aadhar image is uploaded
    const hasAadhar = guest.aadhar_image_url && guest.aadhar_image_url.trim() !== '';

    // Check if profile photo is uploaded
    const hasProfilePhoto = guest.profile_photo && guest.profile_photo.trim() !== '';

    console.log('Profile check - Has email:', hasEmail);
    console.log('Profile check - Has phone:', hasPhone);
    console.log('Profile check - Has Aadhar:', hasAadhar);
    console.log('Profile check - Has Profile Photo:', hasProfilePhoto);

    // Return true if ALL required fields are present
    // You can modify this condition based on your requirements
    return hasEmail && hasPhone && hasAadhar && hasProfilePhoto;
  };

  // Main function to check and validate user profile
  const checkUserProfile = async () => {
    setIsCheckingProfile(true);

    try {
      // Get guest ID from access token
      const guestIdFromToken = getGuestIdFromToken();
      setGuestId(guestIdFromToken);

      if (!guestIdFromToken) {
        console.log('No guest ID found in token');
        setShowProfileModal(true); // Show modal if no guest ID
        setIsCheckingProfile(false);
        return;
      }

      // Fetch guest details from API
      const guest = await fetchGuestDetails(guestIdFromToken);

      if (guest) {
        setGuestData(guest);
        setUserData({
          email: guest.emailid,
          phone: guest.phonenumber,
          firstname: guest.firstname,
          lastname: guest.lastname,
          aadhar_image_url: guest.aadhar_image_url,
          profile_photo: guest.profile_photo,
        });

        // Check if profile is complete
        const profileComplete = isProfileComplete(guest);
        console.log('Profile complete:', profileComplete);

        if (!profileComplete) {
          setShowProfileModal(true);
        } else {
          setShowProfileModal(false);
        }
      } else {
        // If guest not found, show modal
        console.log('Guest not found');
        setShowProfileModal(true);
      }
    } catch (error) {
      console.error('Error checking user profile:', error);
      // Show modal on error as fallback
      setShowProfileModal(true);
    } finally {
      setIsCheckingProfile(false);
    }
  };

  // Force a check with delay to ensure everything is loaded
  useEffect(() => {
    const checkProfileWithDelay = () => {
      // First check immediately
      checkUserProfile();

      // Second check after a short delay to ensure everything is loaded
      const delayCheck = setTimeout(() => {
        console.log('Delay check - Checking profile again after delay');
        // Only recheck if modal is not already open and we have a guest ID
        if (!showProfileModal && guestId) {
          checkUserProfile();
        }
      }, 500);

      return () => clearTimeout(delayCheck);
    };

    checkProfileWithDelay();
  }, []); // Run once on mount

  // Add an event listener for storage changes (in case data is set in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'access_token') {
        console.log('Token changed - rechecking profile');
        if (e.newValue) {
          checkUserProfile();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Navigation guard function
  const handleNavigation = (path) => {
    // Check if guestData is available and profile is complete
    const profileComplete = guestData ? isProfileComplete(guestData) : false;

    if (!profileComplete) {
      // Store the path user was trying to navigate to
      setPendingNavigation(path);
      setShowProfileModal(true);
      return false; // Navigation prevented
    } else {
      navigate(path);
      return true; // Navigation allowed
    }
  };

  const handleCompleteProfile = () => {
    setShowProfileModal(false);
    // Navigate to the create user page
    navigate('/create-user-later');
  };

  const handleContinueWithoutProfile = () => {
    setShowProfileModal(false);
    // If there was a pending navigation, navigate to it after user chooses to continue
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const options = [
    {
      id: 'due-for-checkin',
      title: 'Due for Check-In',
      description: 'View all reservations that are due for check-in',
      icon: <FaSuitcaseRolling size={32} className="text-white" />,
      component: <DueForCheckIn />,
      fastest: false,
      new: false,
      requiresProfile: false, // This option might not need profile check
    },
    {
      id: 'qr-scanning',
      title: 'QR Scanning',
      description: 'Scan the qr code to quickly access your reservation',
      icon: <AiOutlineQrcode size={32} className="text-white" />,
      component: <QRScanner />,
      fastest: true,
      new: false,
      requiresProfile: true, // This might need profile check
    },
    {
      id: 'reservation-id',
      title: 'Reservation Number',
      description: ' Enter the reservation number',
      icon: <HiOutlineIdentification size={32} className="text-white" />,
      component: <Booking />,
      fastest: false,
      new: false,
      requiresProfile: false,
    },
    {
      id: 'facial-recognition',
      title: 'Facial Recognition',
      description: 'Use facial recognition for seamless check-in',
      icon: <FaRegSmile size={32} className="text-white" />,
      component: <SearchReservation />,
      fastest: false,
      new: true,
      requiresProfile: true, // This might need profile check
    },
  ];

  const handleOptionClick = (option) => {
    // Check if this option requires profile completion
    if (option.requiresProfile) {
      const profileComplete = guestData ? isProfileComplete(guestData) : false;
      if (!profileComplete) {
        setPendingNavigation(null);
        setShowProfileModal(true);
        return;
      }
    }

    setActiveOption(option.id);
  };

  // For any other navigation links in the component
  const handleBackButtonClick = (e) => {
    e.preventDefault();
    const profileComplete = guestData ? isProfileComplete(guestData) : false;
    if (!profileComplete) {
      setPendingNavigation('/check-in/options');
      setShowProfileModal(true);
    } else {
      navigate('/check-in/options');
    }
  };

  const getActiveComponent = () => {
    const option = options.find((opt) => opt.id === activeOption);
    return option ? option.component : <Booking />;
  };

  const shouldShowBookingBox = ['qr-scanning', 'facial-recognition'].includes(activeOption);

  // Show loading state while checking profile
  if (isCheckingProfile) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen overflow-hidden">
      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onCompleteProfile={handleCompleteProfile}
        onContinueWithoutProfile={handleContinueWithoutProfile}
        userData={userData}
        guestData={guestData}
      />

      {/* Back Button - Fixed position */}
      <div className="absolute top-24 left-8 z-50">
        <Link
          to="/check-in/options"
          onClick={handleBackButtonClick}
          className="text-black hover:text-gray-700 transition font-semibold text-2xl"
        >
          <IoMdArrowRoundBack size={32} />
        </Link>
      </div>

      {/* Main Content */}
      <div className="p-16 pt-20 pb-10">
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-black font-poppins mb-1">Check-In</h1>
          <p className="text-lg font-poppins font-semibold text-gray-700">
            Select how you'd like to proceed with your reservation
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Options Sidebar */}
          <div className="w-full lg:w-2/5 xl:w-2/6">
            <div className="bg-gray-50 rounded-lg shadow-md p-4">
              {options.map((option) => (
                <div
                  key={option.id}
                  className={`mb-4 last:mb-0 p-4 font-poppins rounded-lg cursor-pointer transition-all ${
                    activeOption === option.id
                      ? 'bg-black text-white'
                      : 'bg-white hover:bg-gray-100'
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                        activeOption === option.id ? 'bg-white' : 'bg-black'
                      }`}
                    >
                      {React.cloneElement(option.icon, {
                        className: activeOption === option.id ? 'text-black' : 'text-white',
                      })}
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg">{option.title}</h3>
                        {option.fastest && (
                          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded ml-2 whitespace-nowrap">
                            Fastest
                          </span>
                        )}
                        {option.new && (
                          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-4 py-0.5 rounded ml-2 whitespace-nowrap">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm">{option.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Option Content */}
          <div className="w-full lg:w-5/6">
            <div className="flex flex-col gap-4">
              {/* Top Box */}
              <div className="bg-white rounded-lg shadow-md border border-gray-200 min-h-[500px] p-6 flex items-center justify-center">
                <div className="w-full">{getActiveComponent()}</div>
              </div>

              {/* Optional "OR" Divider and Manual Booking */}
              {shouldShowBookingBox && (
                <>
                  {/* Centered OR Separator */}
                  <div className="flex items-center justify-center space-x-4 w-full">
                    <div className="flex-grow h-px bg-gray-300"></div>
                    <span className="text-gray-500 font-semibold uppercase text-sm whitespace-nowrap">
                      Or Alternatively enter your details here
                    </span>
                    <div className="flex-grow h-px bg-gray-300"></div>
                  </div>

                  {/* Manual Booking Box */}
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-3">
                    <div className="w-full">
                      <Booking />
                    </div>
                  </div>
                </>
              )}

              <div className="text-start mt-2 ml-10 ">
                <span className="text-sm font-semibold uppercase text-black">
                  In case you need any assistance,&nbsp;
                  <span className="block sm:inline">please contact our Front Desk or call:</span>
                  &nbsp;
                  <a href={`tel:${propertyData.phone}`} className="underline hover:text-gray-700">
                    {propertyData.phone}
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationOptions;

// import React, { useState, useEffect } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import { IoMdArrowRoundBack } from 'react-icons/io';
// import { AiOutlineQrcode } from 'react-icons/ai';
// import { FaRegSmile } from 'react-icons/fa';
// import { HiOutlineIdentification } from 'react-icons/hi';
// import { FaUserAlt } from 'react-icons/fa';
// import { FaSuitcaseRolling } from 'react-icons/fa';

// // Import your existing components
// import Booking from './Booking-Options/Booking';
// import SearchReservation from './Facial-Recognition/SearchREservation';
// import QRScanner from './QR-code/QRScanner';
// import SearchByName from '../../common/SearchByName';
// import DueForCheckIn from '../../common/DueForCheckIn';
// import ProfileCompletionModal from './ProfileCompletionModal';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const ReservationOptions = () => {
//   const navigate = useNavigate();
//   const [activeOption, setActiveOption] = useState('due-for-checkin');
//   const [propertyData, setPropertyData] = useState({
//     phone: '', // Default value
//   });
//   const [showProfileModal, setShowProfileModal] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const [pendingNavigation, setPendingNavigation] = useState(null);
//   const [isCheckingProfile, setIsCheckingProfile] = useState(true);

//   useEffect(() => {
//     const fetchPropertyData = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/properties/1`);
//         const data = await response.json();
//         setPropertyData(data);
//       } catch (error) {
//         console.error('Error fetching property data:', error);
//       }
//     };

//     fetchPropertyData();
//   }, []);

//   // Function to check if user profile is complete
//   const isProfileComplete = () => {
//     try {
//       const userDataCache = localStorage.getItem('userDataCache');
//       console.log('Checking profile completion:', userDataCache);

//       if (userDataCache) {
//         const parsedData = JSON.parse(userDataCache);
//         const userInfo = parsedData.data;

//         // Check if email and phone are present
//         const hasEmail = userInfo.email && userInfo.email.trim() !== '';
//         const hasPhone = userInfo.phone_number && userInfo.phone_number.toString().trim() !== '';

//         console.log('Profile check - Has email:', hasEmail);
//         console.log('Profile check - Has phone:', hasPhone);

//         // Return true if profile is complete (both email and phone exist)
//         // Change this condition based on your requirements
//         return hasEmail && hasPhone;
//       }
//       return false;
//     } catch (error) {
//       console.error('Error checking profile completion:', error);
//       return false;
//     }
//   };

//   // Force a check with delay to ensure localStorage is loaded
//   useEffect(() => {
//     const checkUserProfileWithDelay = () => {
//       setIsCheckingProfile(true);

//       // First check immediately
//       const immediateCheck = () => {
//         try {
//           const userDataCache = localStorage.getItem('userDataCache');
//           console.log('Immediate check - userDataCache:', userDataCache);

//           if (userDataCache) {
//             const parsedData = JSON.parse(userDataCache);
//             console.log('Immediate check - Parsed user data:', parsedData);

//             const userInfo = parsedData.data;
//             setUserData(userInfo);

//             // Check if profile is incomplete
//             const hasEmail = userInfo.email && userInfo.email.trim() !== '';
//             const hasPhone =
//               userInfo.phone_number && userInfo.phone_number.toString().trim() !== '';

//             console.log('Immediate check - Has email:', hasEmail);
//             console.log('Immediate check - Has phone:', hasPhone);

//             // Show modal if profile is incomplete
//             if (!hasEmail || !hasPhone) {
//               console.log('Immediate check - Showing profile modal - profile incomplete');
//               setShowProfileModal(true);
//             }
//           } else {
//             console.log('Immediate check - No userDataCache found in localStorage');
//             // Optionally show modal if no user data exists
//             // Uncomment the next line if you want to show modal when no data exists
//             // setShowProfileModal(true);
//           }
//         } catch (error) {
//           console.error('Immediate check - Error checking user profile:', error);
//         } finally {
//           setIsCheckingProfile(false);
//         }
//       };

//       immediateCheck();

//       // Second check after a short delay to ensure everything is loaded
//       const delayCheck = setTimeout(() => {
//         console.log('Delay check - Checking profile again after delay');
//         try {
//           const userDataCache = localStorage.getItem('userDataCache');
//           console.log('Delay check - userDataCache:', userDataCache);

//           if (userDataCache) {
//             const parsedData = JSON.parse(userDataCache);
//             const userInfo = parsedData.data;
//             setUserData(userInfo);

//             const hasEmail = userInfo.email && userInfo.email.trim() !== '';
//             const hasPhone =
//               userInfo.phone_number && userInfo.phone_number.toString().trim() !== '';

//             console.log('Delay check - Has email:', hasEmail);
//             console.log('Delay check - Has phone:', hasPhone);

//             if (!hasEmail || !hasPhone) {
//               console.log('Delay check - Showing profile modal - profile incomplete');
//               setShowProfileModal(true);
//             }
//           }
//         } catch (error) {
//           console.error('Delay check - Error checking user profile:', error);
//         }
//       }, 500); // 500ms delay

//       return () => clearTimeout(delayCheck);
//     };

//     checkUserProfileWithDelay();
//   }, []);

//   // Add an event listener for storage changes (in case data is set in another tab)
//   useEffect(() => {
//     const handleStorageChange = (e) => {
//       if (e.key === 'userDataCache') {
//         console.log('Storage changed - rechecking profile');
//         if (e.newValue) {
//           try {
//             const parsedData = JSON.parse(e.newValue);
//             const userInfo = parsedData.data;
//             setUserData(userInfo);

//             const hasEmail = userInfo.email && userInfo.email.trim() !== '';
//             const hasPhone =
//               userInfo.phone_number && userInfo.phone_number.toString().trim() !== '';

//             if (!hasEmail || !hasPhone) {
//               setShowProfileModal(true);
//             } else {
//               setShowProfileModal(false);
//             }
//           } catch (error) {
//             console.error('Error parsing storage data:', error);
//           }
//         }
//       }
//     };

//     window.addEventListener('storage', handleStorageChange);
//     return () => window.removeEventListener('storage', handleStorageChange);
//   }, []);

//   // Navigation guard function
//   const handleNavigation = (path) => {
//     if (!isProfileComplete()) {
//       // Store the path user was trying to navigate to
//       setPendingNavigation(path);
//       setShowProfileModal(true);
//       return false; // Navigation prevented
//     } else {
//       navigate(path);
//       return true; // Navigation allowed
//     }
//   };

//   const handleCompleteProfile = () => {
//     setShowProfileModal(false);
//     // Navigate to the create user page
//     navigate('/create-user-later');
//   };

//   const handleContinueWithoutProfile = () => {
//     setShowProfileModal(false);
//     // If there was a pending navigation, navigate to it after user chooses to continue
//     if (pendingNavigation) {
//       navigate(pendingNavigation);
//       setPendingNavigation(null);
//     }
//   };

//   const options = [
//     {
//       id: 'due-for-checkin',
//       title: 'Due for Check-In',
//       description: 'View all reservations that are due for check-in',
//       icon: <FaSuitcaseRolling size={32} className="text-white" />,
//       component: <DueForCheckIn />,
//       fastest: false,
//       new: false,
//       requiresProfile: false, // This option might not need profile check
//     },
//     {
//       id: 'qr-scanning',
//       title: 'QR Scanning',
//       description: 'Scan the qr code to quickly access your reservation',
//       icon: <AiOutlineQrcode size={32} className="text-white" />,
//       component: <QRScanner />,
//       fastest: true,
//       new: false,
//       requiresProfile: true, // This might need profile check
//     },
//     {
//       id: 'reservation-id',
//       title: 'Reservation Number',
//       description: ' Enter the reservation number',
//       icon: <HiOutlineIdentification size={32} className="text-white" />,
//       component: <Booking />,
//       fastest: false,
//       new: false,
//       requiresProfile: false,
//     },
//     {
//       id: 'facial-recognition',
//       title: 'Facial Recognition',
//       description: 'Use facial recognition for seamless check-in',
//       icon: <FaRegSmile size={32} className="text-white" />,
//       component: <SearchReservation />,
//       fastest: false,
//       new: true,
//       requiresProfile: true, // This might need profile check
//     },
//   ];

//   const handleOptionClick = (option) => {
//     // Check if this option requires profile completion
//     if (option.requiresProfile && !isProfileComplete()) {
//       setPendingNavigation(null); // No navigation path, just showing modal
//       setShowProfileModal(true);
//       return;
//     }

//     setActiveOption(option.id);
//   };

//   // For any other navigation links in the component
//   const handleBackButtonClick = (e) => {
//     e.preventDefault();
//     if (!isProfileComplete()) {
//       setPendingNavigation('/check-in/options');
//       setShowProfileModal(true);
//     } else {
//       navigate('/check-in/options');
//     }
//   };

//   const getActiveComponent = () => {
//     const option = options.find((opt) => opt.id === activeOption);
//     return option ? option.component : <Booking />;
//   };

//   const shouldShowBookingBox = ['qr-scanning', 'facial-recognition'].includes(activeOption);

//   // Show loading state while checking profile
//   if (isCheckingProfile) {
//     return (
//       <div className="bg-white min-h-screen flex items-center justify-center">
//         <div className="text-center">
//           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
//           <p className="mt-4 text-gray-600">Loading...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white min-h-screen overflow-hidden">
//       {/* Profile Completion Modal */}
//       <ProfileCompletionModal
//         isOpen={showProfileModal}
//         onClose={() => setShowProfileModal(false)}
//         onCompleteProfile={handleCompleteProfile}
//         onContinueWithoutProfile={handleContinueWithoutProfile}
//         userData={userData}
//       />

//       {/* Back Button - Fixed position */}
//       <div className="absolute top-24 left-8 z-50">
//         <Link
//           to="/check-in/options"
//           onClick={handleBackButtonClick}
//           className="text-black hover:text-gray-700 transition font-semibold text-2xl"
//         >
//           <IoMdArrowRoundBack size={32} />
//         </Link>
//       </div>

//       {/* Main Content */}
//       <div className="p-16 pt-20 pb-10">
//         <div className="text-center mb-4">
//           <h1 className="text-3xl font-bold text-black font-poppins mb-1">Check-In</h1>
//           <p className="text-lg font-poppins font-semibold text-gray-700">
//             Select how you'd like to proceed with your reservation
//           </p>
//         </div>

//         <div className="flex flex-col lg:flex-row gap-4">
//           {/* Options Sidebar */}
//           <div className="w-full lg:w-2/5 xl:w-2/6">
//             <div className="bg-gray-50 rounded-lg shadow-md p-4">
//               {options.map((option) => (
//                 <div
//                   key={option.id}
//                   className={`mb-4 last:mb-0 p-4 font-poppins rounded-lg cursor-pointer transition-all ${
//                     activeOption === option.id
//                       ? 'bg-black text-white'
//                       : 'bg-white hover:bg-gray-100'
//                   }`}
//                   onClick={() => handleOptionClick(option)}
//                 >
//                   <div className="flex items-center">
//                     <div
//                       className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
//                         activeOption === option.id ? 'bg-white' : 'bg-black'
//                       }`}
//                     >
//                       {React.cloneElement(option.icon, {
//                         className: activeOption === option.id ? 'text-black' : 'text-white',
//                       })}
//                     </div>
//                     <div className="flex-grow">
//                       <div className="flex items-center justify-between">
//                         <h3 className="font-bold text-lg">{option.title}</h3>
//                         {option.fastest && (
//                           <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded ml-2 whitespace-nowrap">
//                             Fastest
//                           </span>
//                         )}
//                         {option.new && (
//                           <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-4 py-0.5 rounded ml-2 whitespace-nowrap">
//                             New
//                           </span>
//                         )}
//                       </div>
//                       <p className="text-sm">{option.description}</p>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Active Option Content */}
//           <div className="w-full lg:w-5/6">
//             <div className="flex flex-col gap-4">
//               {/* Top Box */}
//               <div className="bg-white rounded-lg shadow-md border border-gray-200 min-h-[500px] p-6 flex items-center justify-center">
//                 <div className="w-full">{getActiveComponent()}</div>
//               </div>

//               {/* Optional "OR" Divider and Manual Booking */}
//               {shouldShowBookingBox && (
//                 <>
//                   {/* Centered OR Separator */}
//                   <div className="flex items-center justify-center space-x-4 w-full">
//                     <div className="flex-grow h-px bg-gray-300"></div>
//                     <span className="text-gray-500 font-semibold uppercase text-sm whitespace-nowrap">
//                       Or Alternatively enter your details here
//                     </span>
//                     <div className="flex-grow h-px bg-gray-300"></div>
//                   </div>

//                   {/* Manual Booking Box */}
//                   <div className="bg-white rounded-lg shadow-md border border-gray-200 p-3">
//                     <div className="w-full">
//                       <Booking />
//                     </div>
//                   </div>
//                 </>
//               )}

//               <div className="text-start mt-2 ml-10 ">
//                 <span className="text-sm font-semibold uppercase text-black">
//                   In case you need any assistance,&nbsp;
//                   <span className="block sm:inline">please contact our Front Desk or call:</span>
//                   &nbsp;
//                   <a href={`tel:${propertyData.phone}`} className="underline hover:text-gray-700">
//                     {propertyData.phone}
//                   </a>
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ReservationOptions;
